// Root-cause classifier for failed e-invoicing documents.
//
// Ported 1:1 from the offline MBR pipeline `analyze_invoice_errors.py` (_RULES ladder)
// so the dashboard's Failure Analysis view matches the monthly MBR slide classification.
// Rules are evaluated in order; the first matching rule wins, otherwise "Other".

export type RootCause =
    | 'Customer tax ID missing'
    | 'Customer tax ID invalid'
    | 'Address validation error'
    | 'Recipient not found / directory mismatch'
    | 'VAT / tax calculation error'
    | 'Buyer rejected'
    | 'Invalid tax category or tax code'
    | 'Schema / format validation error'
    | 'Supplier portal rejection (Ariba)'
    | 'Other';

// Display / ordering used across KPIs, breakdowns and the heatmap.
export const ROOT_CAUSE_ORDER: RootCause[] = [
    'Customer tax ID missing',
    'Customer tax ID invalid',
    'Address validation error',
    'Recipient not found / directory mismatch',
    'VAT / tax calculation error',
    'Buyer rejected',
    'Invalid tax category or tax code',
    'Schema / format validation error',
    'Supplier portal rejection (Ariba)',
    'Other',
];

// Evaluation order mirrors the Python _RULES list (NOT ROOT_CAUSE_ORDER).
const RULES: { label: RootCause; pattern: RegExp }[] = [
    {
        label: 'Customer tax ID missing',
        pattern: /tax.{0,20}(id|number|reg).{0,20}(miss|not.{0,5}provid|empty|blank|absent|required)/i,
    },
    {
        label: 'Customer tax ID invalid',
        pattern:
            /tax.{0,20}(id|number|reg).{0,30}(invalid|not.{0,5}valid|inactive|cancel|wrong|incorrect|unrecogni|fail)|CPF.?\/.?CNPJ.{0,40}(doesn.t correspond|not.{0,5}match|invalid)|CCM.{0,30}(more than one|required|inform|is not valid|invalid|not.{0,5}match)|Invalid CCM|more than one CCM|RFC.{0,20}(receptor|receiver).{0,30}(no existe|not exist|not found)|CFDI4014[35]|is a budget user|not on the list of active users|ccmType/i,
    },
    {
        label: 'Address validation error',
        pattern:
            /(address|ship.?to|postal|zip|city|street|pincode|statecode).{0,30}(invalid|error|fail|missing|mismatch|not.{0,5}found|not.{0,5}valid|reject|doesn.t match|does not belong|does not exist)|(address|ship.?to).{0,15}(is not valid)|DomicilioFiscal.{0,30}(receptor|receiver)|CFDI40148|governate is required|building number is required/i,
    },
    {
        label: 'Recipient not found / directory mismatch',
        pattern:
            /(recipient|receiver|party).{0,30}(not.{0,5}found|unknown|mismatch|unregister|directory|lookup.{0,5}fail)|recipient.{0,20}do.{0,5}not.{0,10}support|receiver.{0,20}do.{0,5}not.{0,10}support|because the recipien|Company with identifier.{0,20}not found|registered with VAT number.{0,60}but send documents/i,
    },
    {
        label: 'VAT / tax calculation error',
        pattern:
            /(vat|tax).{0,30}(calc|amount|sum|total|rate|percent).{0,20}(error|mismatch|invalid|incorrect|fail|differ)|line.?item.{0,10}(total|amount).{0,20}(invalid|doesn.t match|incorrect|mismatch)|TotAmt.{0,20}should be multiple|TotItemVal.{0,20}(incorrect|invalid)/i,
    },
    {
        label: 'Buyer rejected',
        pattern: /buyer.{0,10}reject/i,
    },
    {
        label: 'Invalid tax category or tax code',
        pattern:
            /tax.{0,25}(categor|classif|code|scheme).{0,20}(invalid|unknown|missing|unsupport|incorrect|unrecogni|not.{0,5}valid|not.{0,5}provid)/i,
    },
    {
        label: 'Schema / format validation error',
        pattern:
            /(schema|xsd|xml|format|syntax|peppol|ubl|structure|element|attribute|namespace|validation).{0,30}(error|fail|invalid|reject|violat|mismatch)|does not satisfy requirements.{0,20}(format|PEPPOL|recipient)/i,
    },
    {
        label: 'Supplier portal rejection (Ariba)',
        pattern:
            /auth failed on supplier.{0,20}(status reason|comments)|failed on supplier.{0,10}Additional details.{0,10}status reason|Additional details.{0,5}status reason.{0,5}with comments/i,
    },
];

/**
 * Classify a failure ErrorMessage into a root-cause bucket.
 * Returns the first matching rule label, or "Other" when nothing matches / message is empty.
 */
export function classifyRootCause(message: string | undefined | null): RootCause {
    if (typeof message !== 'string' || message.trim() === '') {
        return 'Other';
    }
    for (const rule of RULES) {
        if (rule.pattern.test(message)) {
            return rule.label;
        }
    }
    return 'Other';
}
